import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useData } from '../context/DataContext';
import { adminService } from '../services/api';
import RedemptionCodeManager from './admin/RedemptionCodeManager';

const {
  FiUsers, FiShield, FiFolder, FiChevronDown, FiChevronRight,
  FiSearch, FiArrowLeft, FiCheck, FiBook, FiCheckCircle,
  FiClock, FiAlertCircle, FiRefreshCw, FiSliders, FiMinus, FiPlus, FiSave,
  FiKey, FiTrash2, FiX, FiAlertTriangle, FiLock, FiUnlock
} = FiIcons;

const TIER_CONFIG = {
  trial: { label: 'Trial', color: 'bg-amber-100 text-amber-800 border-amber-200' },
  single: { label: 'Single', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  lifetime: { label: 'Lifetime', color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  admin: { label: 'Admin', color: 'bg-purple-100 text-purple-800 border-purple-200' }
};

const AdminUserManagement = ({ onBack }) => {
  const { currentUser } = useData();
  const [users, setUsers] = useState([]);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedTier, setSelectedTier] = useState('all');
  const [expandedUsers, setExpandedUsers] = useState({});
  const [updatingUserId, setUpdatingUserId] = useState(null);
  const [successToast, setSuccessToast] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showCodesSection, setShowCodesSection] = useState(true);
  const [lifetimeProducts, setLifetimeProducts] = useState('');
  const [singleProducts, setSingleProducts] = useState('');
  const [accessCodes, setAccessCodes] = useState('');
  const [newCodeInput, setNewCodeInput] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  // Deletion state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingUser, setDeletingUser] = useState(false);
  const [deletingProjectId, setDeletingProjectId] = useState(null);
  const [lockingProjectId, setLockingProjectId] = useState(null);

  const formatProductsDisplay = (val) => {
    if (!val) return '';
    const trimmed = String(val).trim();
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed.join(', ');
      } catch (e) {}
    }
    return trimmed;
  };

  const fetchUsers = async () => {
    if (!currentUser?.local_id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await adminService.getUsers(currentUser.local_id);
      if (res.success && Array.isArray(res.users)) {
        setUsers(res.users);
        if (Array.isArray(res.codes)) {
          setCodes(res.codes);
        }
        if (res.settings) {
          setLifetimeProducts(formatProductsDisplay(res.settings.skillplate_lifetime_products || ''));
          setSingleProducts(formatProductsDisplay(res.settings.skillplate_single_products || ''));

          let rawCodes = res.settings.access_codes || '';
          try {
            const parsed = JSON.parse(rawCodes);
            if (Array.isArray(parsed)) {
              setAccessCodes(parsed.join('\n'));
            } else {
              setAccessCodes(rawCodes);
            }
          } catch (e) {
            setAccessCodes(rawCodes);
          }
        }
      } else {
        throw new Error(res.error || 'Failed to retrieve users');
      }
    } catch (err) {
      setError(err.message || 'Error loading users.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [currentUser]);

  const toggleExpand = (userId) => {
    setExpandedUsers(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  const handleUpdateTier = async (targetUser, newTier) => {
    if (updatingUserId) return;
    setUpdatingUserId(targetUser.id);
    try {
      const res = await adminService.updateAccess(currentUser.local_id, targetUser.id, newTier);
      if (res.success) {
        setUsers(prev => prev.map(u => u.id === targetUser.id ? { ...u, access_type: newTier } : u));
        setSuccessToast(`Updated ${targetUser.email} to ${newTier.toUpperCase()}`);
        setTimeout(() => setSuccessToast(null), 3000);
      }
    } catch (err) {
      alert(`Failed to update access: ${err.message}`);
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleAdjustSingleAmt = async (targetUser, delta) => {
    if (updatingUserId) return;
    const currentAmt = targetUser.single_amt || 0;
    const nextAmt = Math.max(0, currentAmt + delta);
    setUpdatingUserId(targetUser.id);
    try {
      const res = await adminService.updateAccess(currentUser.local_id, targetUser.id, targetUser.access_type, nextAmt);
      if (res.success) {
        setUsers(prev => prev.map(u => u.id === targetUser.id ? { ...u, single_amt: nextAmt } : u));
        setSuccessToast(`Updated ${targetUser.email} single allowance to ${nextAmt}`);
        setTimeout(() => setSuccessToast(null), 3000);
      }
    } catch (err) {
      alert(`Failed to update single allowance: ${err.message}`);
    } finally {
      setUpdatingUserId(null);
    }
  };

  // ---------------------------------------------------------
  // User & project deletion
  // ---------------------------------------------------------
  const deleteConfirmMatches =
    !!deleteTarget &&
    deleteConfirmText.trim().toLowerCase() === (deleteTarget.email || '').trim().toLowerCase();

  const openDeleteUser = (user) => {
    setDeleteTarget(user);
    setDeleteConfirmText('');
  };

  const closeDeleteUser = () => {
    if (deletingUser) return;
    setDeleteTarget(null);
    setDeleteConfirmText('');
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget || !deleteConfirmMatches || deletingUser) return;
    const target = deleteTarget;
    setDeletingUser(true);
    try {
      const res = await adminService.deleteUser(currentUser.local_id, target.id, deleteConfirmText.trim());
      setUsers(prev => prev.filter(u => u.id !== target.id));
      setExpandedUsers(prev => {
        const next = { ...prev };
        delete next[target.id];
        return next;
      });
      setSuccessToast(res.message || `Deleted ${target.email}`);
      setTimeout(() => setSuccessToast(null), 4000);
      setDeleteTarget(null);
      setDeleteConfirmText('');
    } catch (err) {
      alert(`Failed to delete user: ${err.message}`);
    } finally {
      setDeletingUser(false);
    }
  };

  const handleDeleteProject = async (owner, proj) => {
    if (deletingProjectId) return;
    const title = proj.title || 'Untitled Book';
    const ok = window.confirm(
      `Permanently delete "${title}" from ${owner.email}?\n\n` +
      'Its analysis, exports, and uploaded files will be removed. This cannot be undone.'
    );
    if (!ok) return;

    setDeletingProjectId(proj.id);
    try {
      const res = await adminService.deleteProject(currentUser.local_id, proj.id);
      setUsers(prev => prev.map(u => {
        if (u.id !== owner.id) return u;
        const projects = (u.projects || []).filter(p => p.id !== proj.id);
        return { ...u, projects, project_count: projects.length };
      }));
      setSuccessToast(res.message || `Deleted "${title}"`);
      setTimeout(() => setSuccessToast(null), 4000);
    } catch (err) {
      alert(`Failed to delete project: ${err.message}`);
    } finally {
      setDeletingProjectId(null);
    }
  };

  const handleToggleProjectLock = async (owner, proj) => {
    if (lockingProjectId) return;
    const nextLocked = !Number(proj.metadata_locked);
    setLockingProjectId(proj.id);
    try {
      const res = await adminService.setProjectLock(currentUser.local_id, proj.id, nextLocked);
      const savedLocked = Number(res.metadata_locked ?? (nextLocked ? 1 : 0));
      setUsers(prev => prev.map(u => {
        if (u.id !== owner.id) return u;
        return {
          ...u,
          projects: (u.projects || []).map(p => p.id === proj.id ? { ...p, metadata_locked: savedLocked } : p)
        };
      }));
      setSuccessToast(res.message || (nextLocked ? 'Book Metadata locked' : 'Book Metadata unlocked'));
      setTimeout(() => setSuccessToast(null), 3000);
    } catch (err) {
      alert(`Failed to update lock: ${err.message}`);
    } finally {
      setLockingProjectId(null);
    }
  };

  const handleAddNewCode = () => {
    const code = newCodeInput.trim().toUpperCase();
    if (!code) return;
    const existingList = accessCodes.split('\n').map(c => c.trim()).filter(Boolean);
    if (existingList.includes(code)) {
      alert('This code is already in the list.');
      return;
    }
    const updated = [...existingList, code].join('\n');
    setAccessCodes(updated);
    setNewCodeInput('');
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      const codesArray = accessCodes
        .split(/[\r\n,]+/)
        .map(c => c.trim().toUpperCase())
        .filter(Boolean);

      const splitProducts = (str) => {
        return (str || '')
          .split(/[\r\n,;]+/)
          .map(p => p.trim())
          .filter(Boolean);
      };

      const res = await adminService.saveSettings(currentUser.local_id, {
        skillplate_lifetime_products: JSON.stringify(splitProducts(lifetimeProducts)),
        skillplate_single_products: JSON.stringify(splitProducts(singleProducts)),
        access_codes: JSON.stringify(codesArray)
      });
      if (res.success) {
        setSuccessToast('Skillplate product mappings saved!');
        setTimeout(() => setSuccessToast(null), 3000);
      } else {
        throw new Error(res.error || 'Failed to save settings');
      }
    } catch (err) {
      alert(`Error saving settings: ${err.message}`);
    } finally {
      setSavingSettings(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch =
      (user.email || '').toLowerCase().includes(search.toLowerCase()) ||
      (user.name || '').toLowerCase().includes(search.toLowerCase()) ||
      String(user.id).includes(search);
    const matchesTier = selectedTier === 'all' || user.access_type === selectedTier;
    return matchesSearch && matchesTier;
  });

  return (
    <div className="min-h-screen bg-cream p-6 md:p-12 relative">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <button
              onClick={onBack}
              className="inline-flex items-center space-x-2 text-slate-500 hover:text-primary font-bold text-xs uppercase tracking-widest mb-3 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} />
              <span>Return to Dashboard</span>
            </button>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-purple-100 text-purple-700 rounded-2xl flex items-center justify-center">
                <SafeIcon icon={FiShield} className="text-2xl" />
              </div>
              <div>
                <h1 className="text-3xl font-serif text-primary font-bold">Admin Management Portal</h1>
                <p className="text-sm text-slate-500 font-medium">
                  Manage user access levels and review manuscript projects across FormatterPro.
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowCodesSection(!showCodesSection)}
              className={`px-4 py-2.5 border rounded-xl text-xs font-bold uppercase tracking-wider flex items-center space-x-2 shadow-sm transition-all ${
                showCodesSection ? 'bg-purple-100 text-purple-800 border-purple-300' : 'bg-white text-slate-700 border-slate-200'
              }`}
            >
              <SafeIcon icon={FiKey} />
              <span>{showCodesSection ? 'Hide Codes' : 'Redemption Codes'}</span>
            </button>

            <button
              onClick={() => setShowSettings(!showSettings)}
              className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-700 hover:border-primary text-xs font-bold uppercase tracking-wider flex items-center space-x-2 shadow-sm"
            >
              <SafeIcon icon={FiSliders} />
              <span>{showSettings ? 'Hide Settings' : 'Skillplate Settings'}</span>
            </button>

            <button
              onClick={fetchUsers}
              disabled={loading}
              className="self-start md:self-auto px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-700 hover:border-primary text-xs font-bold uppercase tracking-wider flex items-center space-x-2 shadow-sm"
            >
              <SafeIcon icon={FiRefreshCw} className={loading ? 'animate-spin' : ''} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Success Toast */}
        <AnimatePresence>
          {successToast && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-6 p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl text-sm font-semibold flex items-center space-x-2"
            >
              <SafeIcon icon={FiCheckCircle} className="text-emerald-600" />
              <span>{successToast}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Dedicated Redemption Codes Manager Section */}
        {showCodesSection && (
          <RedemptionCodeManager
            adminUserId={currentUser?.local_id}
            codes={codes}
            onRefresh={fetchUsers}
          />
        )}

        {/* Skillplate Product Settings Panel */}
        {showSettings && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-white rounded-2xl border border-purple-200 p-6 shadow-sm mb-8 space-y-4"
          >
            <div className="flex items-center space-x-2 mb-2">
              <SafeIcon icon={FiSliders} className="text-purple-600 text-lg" />
              <h3 className="font-bold text-slate-800 text-base">Assign Skillplate Product Names to Tiers</h3>
            </div>
            <form onSubmit={handleSaveSettings} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1">
                  Lifetime Access Products (comma-separated):
                </label>
                <input
                  type="text"
                  value={lifetimeProducts}
                  onChange={e => setLifetimeProducts(e.target.value)}
                  placeholder="formatterpro lifetime, formatpro lifetime"
                  className="w-full p-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1">
                  Single Access Products (comma-separated):
                </label>
                <input
                  type="text"
                  value={singleProducts}
                  onChange={e => setSingleProducts(e.target.value)}
                  placeholder="formatterpro single, formatpro single"
                  className="w-full p-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-primary"
                />
              </div>

              {/* Running list of access codes */}
              <div className="pt-2 border-t border-purple-100">
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center space-x-1">
                    <SafeIcon icon={FiKey} className="text-purple-600" />
                    <span>Access Codes (running list):</span>
                  </label>
                  <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">One code per line</span>
                </div>
                <div className="flex space-x-2 mb-2">
                  <input
                    type="text"
                    value={newCodeInput}
                    onChange={e => setNewCodeInput(e.target.value.toUpperCase())}
                    placeholder="ENTER-NEW-CODE"
                    className="flex-1 p-2 rounded-xl border border-slate-200 text-xs font-mono font-bold uppercase focus:outline-none focus:border-primary"
                  />
                  <button
                    type="button"
                    onClick={handleAddNewCode}
                    className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-bold uppercase tracking-wider flex items-center space-x-1"
                  >
                    <SafeIcon icon={FiPlus} />
                    <span>Add</span>
                  </button>
                </div>
                <textarea
                  rows={3}
                  value={accessCodes}
                  onChange={e => setAccessCodes(e.target.value.toUpperCase())}
                  placeholder="PROMO-2024&#10;GWA-SINGLE-PASS"
                  className="w-full p-2.5 rounded-xl border border-slate-200 text-xs font-mono focus:outline-none focus:border-primary"
                />
              </div>

              <button
                type="submit"
                disabled={savingSettings}
                className="px-5 py-2.5 bg-primary text-white rounded-xl text-xs font-bold uppercase tracking-wider flex items-center space-x-2"
              >
                <SafeIcon icon={FiSave} />
                <span>{savingSettings ? 'Saving...' : 'Save Product Assignments'}</span>
              </button>
            </form>
          </motion.div>
        )}

        {/* Filter bar & Metrics */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm mb-8 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pb-4 border-b border-slate-100">
            <div className="bg-slate-50 p-4 rounded-xl">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Total Users</p>
              <p className="text-2xl font-serif font-bold text-slate-800">{users.length}</p>
            </div>
            <div className="bg-amber-50 p-4 rounded-xl">
              <p className="text-xs font-bold text-amber-600 uppercase tracking-widest">Trial Users</p>
              <p className="text-2xl font-serif font-bold text-amber-800">
                {users.filter(u => (u.access_type || 'trial') === 'trial').length}
              </p>
            </div>
            <div className="bg-blue-50 p-4 rounded-xl">
              <p className="text-xs font-bold text-blue-600 uppercase tracking-widest">Single Access</p>
              <p className="text-2xl font-serif font-bold text-blue-800">
                {users.filter(u => u.access_type === 'single').length}
              </p>
            </div>
            <div className="bg-emerald-50 p-4 rounded-xl">
              <p className="text-xs font-bold text-emerald-600 uppercase tracking-widest">Lifetime Access</p>
              <p className="text-2xl font-serif font-bold text-emerald-800">
                {users.filter(u => u.access_type === 'lifetime').length}
              </p>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 w-full">
              <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name, email, or user ID..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-11 pr-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:border-primary text-sm font-medium"
              />
            </div>
            <div className="flex items-center space-x-2 w-full md:w-auto overflow-x-auto pb-1">
              {['all', 'trial', 'single', 'lifetime', 'admin'].map(tier => (
                <button
                  key={tier}
                  onClick={() => setSelectedTier(tier)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold uppercase tracking-wider border transition-all ${
                    selectedTier === tier
                      ? 'bg-primary text-white border-primary shadow-sm'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                  }`}
                >
                  {tier}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* User List Table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-16 text-center text-slate-400">
              <SafeIcon icon={FiRefreshCw} className="text-3xl animate-spin mx-auto mb-2 text-primary" />
              <p className="text-sm font-medium">Loading authors & projects...</p>
            </div>
          ) : error ? (
            <div className="p-12 text-center text-red-500">
              <SafeIcon icon={FiAlertCircle} className="text-3xl mx-auto mb-2" />
              <p className="font-semibold text-sm">{error}</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-16 text-center text-slate-400">
              <SafeIcon icon={FiUsers} className="text-3xl mx-auto mb-2 opacity-50" />
              <p className="font-medium text-sm">No users matched your query.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredUsers.map(user => {
                const isExpanded = !!expandedUsers[user.id];
                const tier = user.access_type || 'trial';
                const tierStyle = TIER_CONFIG[tier] || TIER_CONFIG.trial;

                return (
                  <div key={user.id} className="transition-colors hover:bg-slate-50/50">
                    <div className="p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
                      {/* User Info */}
                      <div className="flex items-start space-x-4">
                        <button
                          onClick={() => toggleExpand(user.id)}
                          className="p-2 text-slate-400 hover:text-primary rounded-lg transition-colors mt-0.5"
                          title="Toggle Projects Folder"
                        >
                          <SafeIcon icon={isExpanded ? FiChevronDown : FiChevronRight} className="text-lg" />
                        </button>
                        <div>
                          <div className="flex items-center space-x-3">
                            <h3 className="font-bold text-slate-800 text-base">{user.name || 'Unnamed Author'}</h3>
                            <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${tierStyle.color}`}>
                              {tierStyle.label}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 font-mono mt-0.5">{user.email}</p>
                          <div className="flex items-center space-x-4 mt-2 text-[11px] text-slate-400 font-medium">
                            <span>ID: #{user.id}</span>
                            <span>•</span>
                            <span>Hub ID: {user.hub_id || 'N/A'}</span>
                            <span>•</span>
                            <span className="flex items-center space-x-1">
                              <SafeIcon icon={FiFolder} />
                              <span>{user.project_count || 0} Projects</span>
                            </span>
                            {tier === 'single' && (
                              <>
                                <span>•</span>
                                <span className="font-bold text-blue-700">Single Limit: {user.single_amt || 0}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Privilege Granting Controls */}
                      <div className="flex items-center space-x-2 self-start md:self-auto">
                        {tier === 'single' && (
                          <div className="flex items-center space-x-1 bg-blue-50 border border-blue-200 rounded-lg p-1 mr-2">
                            <span className="text-[10px] font-bold text-blue-800 uppercase px-1">Conversions:</span>
                            <button
                              onClick={() => handleAdjustSingleAmt(user, -1)}
                              disabled={updatingUserId === user.id}
                              className="w-5 h-5 flex items-center justify-center bg-white rounded text-blue-800 hover:bg-blue-100 text-xs font-bold"
                            >
                              <SafeIcon icon={FiMinus} />
                            </button>
                            <span className="text-xs font-bold px-1 text-blue-900">{user.single_amt || 0}</span>
                            <button
                              onClick={() => handleAdjustSingleAmt(user, 1)}
                              disabled={updatingUserId === user.id}
                              className="w-5 h-5 flex items-center justify-center bg-white rounded text-blue-800 hover:bg-blue-100 text-xs font-bold"
                            >
                              <SafeIcon icon={FiPlus} />
                            </button>
                          </div>
                        )}

                        <div className="flex items-center space-x-2 self-start md:self-auto bg-slate-100 p-1 rounded-xl">
                          {['trial', 'single', 'lifetime', 'admin'].map(t => {
                            const isActive = tier === t;
                            const isProcessing = updatingUserId === user.id;

                            return (
                              <button
                                key={t}
                                disabled={isProcessing}
                                onClick={() => handleUpdateTier(user, t)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-bold capitalize transition-all ${
                                  isActive
                                    ? 'bg-white text-slate-800 shadow-sm font-black'
                                    : 'text-slate-500 hover:text-slate-800 hover:bg-white/60'
                                } disabled:opacity-50`}
                              >
                                {t}
                              </button>
                            );
                          })}
                        </div>

                        {/* Delete user (never shown for admins or for yourself) */}
                        {tier !== 'admin' && String(user.id) !== String(currentUser?.local_id) && (
                          <button
                            onClick={() => openDeleteUser(user)}
                            disabled={updatingUserId === user.id || deletingUser}
                            title="Delete user and all their projects"
                            className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200 transition-all disabled:opacity-50"
                          >
                            <SafeIcon icon={FiTrash2} />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Collapsible Folder for User Manuscripts */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="bg-slate-50 border-t border-slate-100 px-8 py-5"
                        >
                          <div className="flex items-center space-x-2 text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">
                            <SafeIcon icon={FiFolder} />
                            <span>Manuscript Conversion Projects ({user.projects?.length || 0})</span>
                          </div>

                          {(!user.projects || user.projects.length === 0) ? (
                            <p className="text-xs text-slate-400 italic">No manuscript projects created yet.</p>
                          ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                              {user.projects.map(proj => (
                                <div
                                  key={proj.id}
                                  className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs flex items-center justify-between"
                                >
                                  <div className="min-w-0 pr-3">
                                    <h4 className="font-bold text-xs text-slate-800 truncate mb-0.5">
                                      {proj.title || 'Untitled Book'}
                                    </h4>
                                    <p className="text-[11px] text-slate-400 truncate">By {proj.author || 'Author'}</p>
                                    {Number(proj.metadata_locked) === 1 && (
                                      <span className="inline-flex items-center space-x-1 mt-1 px-1.5 py-0.5 rounded-md bg-purple-50 border border-purple-200 text-[9px] font-black uppercase tracking-wider text-purple-700">
                                        <SafeIcon icon={FiLock} />
                                        <span>Metadata locked</span>
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center space-x-1.5 shrink-0">
                                    <span className={`px-2 py-0.5 text-[9px] font-black uppercase tracking-wider rounded-md ${
                                      proj.status === 'exported' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                                    }`}>
                                      {proj.status}
                                    </span>
                                    <button
                                      onClick={() => handleToggleProjectLock(user, proj)}
                                      disabled={!!lockingProjectId}
                                      title={Number(proj.metadata_locked) === 1
                                        ? 'Unlock Book Metadata'
                                        : 'Lock Book Metadata (author can still replace the file, change structure and print format, and export)'}
                                      className={`p-1.5 rounded-md transition-all disabled:opacity-50 ${
                                        Number(proj.metadata_locked) === 1
                                          ? 'text-purple-600 bg-purple-50 hover:bg-purple-100'
                                          : 'text-slate-300 hover:text-purple-600 hover:bg-purple-50'
                                      }`}
                                    >
                                      <SafeIcon
                                        icon={lockingProjectId === proj.id ? FiRefreshCw : (Number(proj.metadata_locked) === 1 ? FiLock : FiUnlock)}
                                        className={`text-xs ${lockingProjectId === proj.id ? 'animate-spin' : ''}`}
                                      />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteProject(user, proj)}
                                      disabled={!!deletingProjectId}
                                      title="Delete this project"
                                      className="p-1.5 rounded-md text-slate-300 hover:text-red-600 hover:bg-red-50 transition-all disabled:opacity-50"
                                    >
                                      <SafeIcon
                                        icon={deletingProjectId === proj.id ? FiRefreshCw : FiTrash2}
                                        className={`text-xs ${deletingProjectId === proj.id ? 'animate-spin' : ''}`}
                                      />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Delete User Confirmation Modal */}
      <AnimatePresence>
        {deleteTarget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeDeleteUser}
            className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl border border-red-200 w-full max-w-md p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-red-100 text-red-600 rounded-xl flex items-center justify-center">
                    <SafeIcon icon={FiAlertTriangle} className="text-xl" />
                  </div>
                  <h3 className="font-bold text-slate-800 text-lg">Delete this user?</h3>
                </div>
                <button
                  onClick={closeDeleteUser}
                  disabled={deletingUser}
                  className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-50"
                  title="Cancel"
                >
                  <SafeIcon icon={FiX} />
                </button>
              </div>

              <p className="text-sm text-slate-600 mb-1">
                This permanently deletes <span className="font-bold text-slate-800">{deleteTarget.email}</span> and
                their {deleteTarget.project_count || 0} project(s), including analyses, exports, and uploaded files.
              </p>
              <p className="text-sm font-semibold text-red-600 mb-4">This cannot be undone.</p>

              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1">
                Type their email to confirm
              </label>
              <input
                type="text"
                autoFocus
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleDeleteUser();
                  if (e.key === 'Escape') closeDeleteUser();
                }}
                placeholder={deleteTarget.email}
                disabled={deletingUser}
                className="w-full p-2.5 rounded-xl border border-slate-200 text-sm font-mono focus:outline-none focus:border-red-400"
              />

              <div className="flex justify-end space-x-2 mt-5">
                <button
                  onClick={closeDeleteUser}
                  disabled={deletingUser}
                  className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-700 text-xs font-bold uppercase tracking-wider disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteUser}
                  disabled={!deleteConfirmMatches || deletingUser}
                  className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold uppercase tracking-wider flex items-center space-x-2 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <SafeIcon icon={deletingUser ? FiRefreshCw : FiTrash2} className={deletingUser ? 'animate-spin' : ''} />
                  <span>{deletingUser ? 'Deleting...' : 'Delete Permanently'}</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminUserManagement;